import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Loader2, Trash2, CalendarClock, X, CheckCircle2 } from "lucide-react";
import { http, apiError, formatDateTime, telHref } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Confirm } from "./Confirm";
import { INQ_STATUS, StatusPill, AssigneePill, FollowPill, toLocalInput } from "./inquiryUi";
import { CommLog } from "./CommLog";
import { ExportPdf } from "./ExportPdf";
import { SharePdf } from "./SharePdf";
import { RequestReview } from "./RequestReview";

const Field = ({ k, v }) => (v ? <div className="grid grid-cols-[110px_1fr] gap-3 py-2"><dt className="text-slate-400">{k}</dt><dd className="text-slate-100 break-words whitespace-pre-line">{v}</dd></div> : null);

export const InquiryDetail = ({ item, team, onChange, onDeleted }) => {
  const { user } = useAuth();
  const owner = user?.role === "owner";
  const [note, setNote] = useState("");
  const [fu, setFu] = useState(toLocalInput(item.follow_up_at));
  const [busy, setBusy] = useState("");

  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      const { data } = await fn();
      if (msg) toast.success(msg);
      onChange(data);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setBusy("");
    }
  };
  const base = `/admin/inquiries/${item.id}`;

  return (
    <div className="space-y-8" data-testid="inquiry-detail">
      <div>
        <p className="font-mono text-xs text-gold" data-testid="inquiry-ref">{item.ref}</p>
        <h2 className="font-display text-3xl mt-1">{item.name}</h2>
        <div className="flex flex-wrap gap-2 mt-3"><StatusPill status={item.status} /><AssigneePill item={item} /><FollowPill item={item} /></div>
        <p className="text-xs text-slate-400 mt-3">Submitted {formatDateTime(item.created_at)} · Last updated {formatDateTime(item.updated_at || item.created_at)}</p>
      </div>

      <section className="space-y-2 text-sm">
        <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-gold hover:underline break-all"><Mail className="w-4 h-4 shrink-0" />{item.email}</a>
        {item.phone && <a href={telHref(item.phone)} className="flex items-center gap-2 text-slate-200 hover:text-gold"><Phone className="w-4 h-4" />{item.phone}</a>}
        <dl className="divide-y divide-white/10 border-y hairline mt-3">
          <Field k="Division" v={item.division} /><Field k="Asset" v={item.asset_title} /><Field k="Stock #" v={item.stock_number} />
          <Field k="Topic" v={item.source === "sourcing_request" ? "Sourcing request" : item.topic} /><Field k={item.source === "sourcing_request" ? "Request" : "Message"} v={item.message} />
        </dl>
      </section>

      <section className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Assigned to</label>
          {owner ? (
            <select className="field" value={item.assigned_to || ""} disabled={busy === "assign"}
              onChange={(e) => run("assign", () => http.post(`${base}/assign`, { user_id: e.target.value || null }), e.target.value ? "Assigned. Team member notified by email." : "Unassigned")}
              data-testid="detail-assign-select">
              <option value="">Unassigned</option>
              {team.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.role})</option>)}
            </select>
          ) : <p className="field !border-transparent !px-0">{item.assigned_name || "Unassigned"}</p>}
        </div>
        <div>
          <label className="label">Status</label>
          <select className="field" value={item.status} disabled={busy === "status"} onChange={(e) => run("status", () => http.patch(base, { status: e.target.value }), "Status updated")} data-testid="detail-status-select">
            {Object.entries(INQ_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
          </select>
        </div>
      </section>

      <section>
        <label className="label flex items-center gap-2"><CalendarClock className="w-4 h-4 text-gold" /> Follow-up date & time</label>
        <div className="flex flex-wrap gap-2">
          <input type="datetime-local" className="field !w-auto" value={fu} onChange={(e) => setFu(e.target.value)} data-testid="detail-followup-input" />
          <button className="btn-gold !py-2.5 !px-4" disabled={!fu || busy === "fu"} onClick={() => run("fu", () => http.patch(base, { follow_up_at: new Date(fu).toISOString() }), "Follow-up scheduled")} data-testid="detail-followup-save">Save</button>
          {item.follow_up_at && <button className="btn-ghost !py-2.5 !px-4 !text-xs" disabled={busy === "fu"} onClick={async () => { if (await run("fu", () => http.post(`${base}/followup/done`), "Follow-up marked done")) setFu(""); }} data-testid="detail-followup-done"><CheckCircle2 className="w-4 h-4" /> Mark done</button>}
          {item.follow_up_at && <button className="btn-ghost !py-2.5 !px-3" title="Clear without marking done" onClick={async () => { if (await run("fu", () => http.patch(base, { clear_follow_up: true }), "Follow-up cleared")) setFu(""); }} data-testid="detail-followup-clear"><X className="w-4 h-4" /></button>}
        </div>
        <p className="text-xs text-slate-400 mt-2">The assigned person receives an email reminder when the follow-up is due. Use Mark done once completed so it counts in the weekly report.</p>
        {(item.followup_log || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-slate-300" data-testid="followup-log">
            {[...item.followup_log].reverse().map((f) => (
              <li key={f.id}><CheckCircle2 className="inline w-3 h-3 text-emerald-400 mr-1" />Due {formatDateTime(f.due_at)} · done {formatDateTime(f.done_at)} by {f.by}{f.on_time ? "" : " (late)"}</li>
            ))}
          </ul>
        )}
      </section>

      <CommLog item={item} onChange={onChange} />

      <ExportPdf item={item} />

      <SharePdf item={item} />

      <RequestReview item={item} />

      <section>
        <p className="eyebrow mb-3">Internal notes <span className="text-slate-400 normal-case tracking-normal">(staff only, never shown to the customer)</span></p>
        <div className="space-y-3 mb-4" data-testid="notes-list">
          {(item.notes_log || []).length ? item.notes_log.map((n) => (
            <div key={n.id} className="border-l-2 border-[var(--gold)] bg-white/[0.03] px-4 py-3">
              <p className="text-sm text-slate-100 whitespace-pre-line">{n.text}</p>
              <p className="text-xs text-slate-400 mt-2">{n.author} · <span className="capitalize">{n.role}</span> · {formatDateTime(n.at)}</p>
            </div>
          )) : <p className="text-sm text-slate-400">No notes yet.</p>}
          {item.notes && <p className="text-xs text-slate-400">Earlier note: {item.notes}</p>}
        </div>
        <textarea rows={3} className="field" placeholder="Add a note for the team…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="detail-note-input" />
        <button className="btn-gold !py-2.5 mt-2" disabled={!note.trim() || busy === "note"} onClick={async () => { if (await run("note", () => http.post(`${base}/notes`, { text: note }), "Note added")) setNote(""); }} data-testid="detail-note-add">
          {busy === "note" && <Loader2 className="w-4 h-4 animate-spin" />} Add note
        </button>
      </section>

      {(item.history || []).length > 0 && (
        <section>
          <p className="eyebrow mb-3">History</p>
          <ol className="space-y-2 text-xs text-slate-300" data-testid="history-list">
            {[...item.history].reverse().map((h, i) => (
              <li key={i}><span className="text-slate-400">{formatDateTime(h.at)}</span> · {h.by}: {h.to !== undefined ? `assignment ${h.from} → ${h.to}` : `status ${INQ_STATUS[h.status_from]?.label || h.status_from} → ${INQ_STATUS[h.status_to]?.label}`}</li>
            ))}
          </ol>
        </section>
      )}

      {owner && (
        <Confirm title="Delete this inquiry?" text="This cannot be undone." onConfirm={() => http.delete(base).then(onDeleted).catch((e) => toast.error(apiError(e)))}>
          <button className="btn-ghost !py-2 !px-4 !text-xs hover:!border-rose-400 hover:!text-rose-300" data-testid="detail-delete"><Trash2 className="w-3 h-3" /> Delete inquiry</button>
        </Confirm>
      )}
    </div>
  );
};
